import {
  ExtraProductInformation,
  ExtraProductInformationElement,
  ExtraProductInformationItem,
  ExtraProductInformationTagType
} from "src/app/data-models/extra-product-information.model";

/** What the block shows: the main rows and, for a sectional, one row per element. */
export class ExtraProductInformationPresentation {
  constructor(
    public rows: ExtraProductInformationItem[] = [],
    public elements: ExtraProductInformationElement[] = [],
  ) {}
}

export interface ExtraProductInformationPresentationResult {
  presentation: ExtraProductInformationPresentation;
  /** Elements of the configuration the backend sent no data for. */
  unmatchedElementIds: string[];
}

const WEIGHT_PATTERN = /^(\d+(?:\.(\d+))?)\s*([^\d\s.,][^\d]*)$/;

/** Element ids of a configuration from left to right, one entry per section - a section used twice is listed twice. */
export function getElementIds(configuration: any): string[] {
  const elements: Record<string, any> = configuration?.elements ?? {};

  return leftToRight(elements)
    .map((key) => String(elements[key]?.element_id ?? ""))
    .filter((elementId) => !!elementId);
}

/**
 * The configurator links every section to its neighbours, so the product reads from a section with a right
 * neighbour and no left one along the right neighbours; for a plain row of sections this is also the order of the
 * "Sections" summary in the menu. Sections no chain reaches (attached at the top, bottom, front or back, standing
 * alone, behind a broken link or in a closed ring) follow in the order of the configuration, so none of them drops
 * out of the table or the total.
 */
function leftToRight(elements: Record<string, any>): string[] {
  const keys = Object.keys(elements);
  const ordered: string[] = [];
  const visited = new Set<string>();
  const heads = keys.filter((key) => elements[key]?.neighbours?.right && !elements[key]?.neighbours?.left);

  heads.forEach((head) => {
    for (let key = head; key && elements[key] && !visited.has(key); key = elements[key].neighbours?.right) {
      visited.add(key);
      ordered.push(key);
    }
  });

  return [...ordered, ...keys.filter((key) => !visited.has(key))];
}

/**
 * A simple product shows its rows as sent. A sectional follows its current configuration: the backend lists every
 * element the product can be built from, the configuration says which ones are used. Every used section gets a row
 * in the element table, a single one too, so the dimensions live only there. The product's shipping weight describes
 * the default sections only, so it is replaced by the total of the used sections, or hidden when there is no total.
 * Until the configuration is known a sectional shows nothing, so the block does not flash an incomplete table.
 * A configured element without data is left out, and then no total is computed - it would be too low.
 * Sections with neither dimensions nor weight give no table.
 */
export function presentExtraProductInformation(
  information: ExtraProductInformation,
  configuration: unknown
): ExtraProductInformationPresentationResult {
  if (information.elements.length === 0) {
    return { presentation: new ExtraProductInformationPresentation(information.items), unmatchedElementIds: [] };
  }
  if (!configuration) {
    return { presentation: new ExtraProductInformationPresentation(), unmatchedElementIds: [] };
  }

  const configuredElementIds = getElementIds(configuration);
  const { shipped, unmatchedElementIds } = shippedElements(information.elements, configuredElementIds);
  const weight = shipped.length === configuredElementIds.length ? totalWeight(shipped) : "";

  const rows = information.items
    .filter((item) => item.slug !== ExtraProductInformationTagType.ShippingDimensions)
    .map((item) => item.slug === ExtraProductInformationTagType.ShippingWeight ? withValue(item, weight) : item)
    .filter((item) => !!item.value);

  return {
    presentation: new ExtraProductInformationPresentation(rows, hasShippingData(shipped) ? shipped : []),
    unmatchedElementIds,
  };
}

function hasShippingData(elements: ExtraProductInformationElement[]): boolean {
  return elements.some((element) => !!element.dimensions || !!element.weight);
}

function shippedElements(
  elements: ExtraProductInformationElement[],
  configuredElementIds: string[]
): { shipped: ExtraProductInformationElement[], unmatchedElementIds: string[] } {
  const elementsById = new Map(elements.map((element) => [element.id, element]));
  const shipped: ExtraProductInformationElement[] = [];
  const unmatchedElementIds: string[] = [];

  configuredElementIds.forEach((elementId) => {
    const element = elementsById.get(elementId);
    if (element) shipped.push(element);
    else if (!unmatchedElementIds.includes(elementId)) unmatchedElementIds.push(elementId);
  });

  return { shipped, unmatchedElementIds };
}

/** Sum of the element weights, only when every element has one in the same unit - a partial sum would mislead. */
function totalWeight(elements: ExtraProductInformationElement[]): string {
  const weights = elements.map((element) => element.weight.match(WEIGHT_PATTERN));
  if (weights.length === 0 || weights.some((weight) => !weight)) return "";

  const unit = weights[0][3].trim();
  if (weights.some((weight) => weight[3].trim().toLowerCase() !== unit.toLowerCase())) return "";

  const decimals = Math.max(...weights.map((weight) => weight[2]?.length ?? 0));
  const total = weights.reduce((sum, weight) => sum + Number(weight[1]), 0);

  return `${total.toFixed(decimals)} ${unit} total`;
}

function withValue(item: ExtraProductInformationItem, value: string): ExtraProductInformationItem {
  return new ExtraProductInformationItem(item.slug, item.name, value);
}
